import * as Sentry from '@sentry/nextjs';

import { GoalPatchModel, VersionedEntityType } from '@models';
import { APIRequestLabel } from '../consts';
import { callHttpDelete, callHttpGet, callHttpPost, callHttpPut } from '../http';

const createGoal = (get, set) => {
  return async (goal: GoalPatchModel): Promise<string | void> => {
    await callHttpPost(get, set, APIRequestLabel.CREATE_GOAL, `/goals`, goal);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_GOAL];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return null;
    }

    return apiCallStatus.response?.entityId;
  };
};

/**
 * @deprecated Use getGoals instead
 */
const getGoalsOld = (get, set) => {
  return async (params?: {
    query?: string;
    semanticQuery?: string;
    filters?: Record<any, boolean>;
  }) => {
    params = params || {};
    let qParams: {
      maxVersionType: VersionedEntityType;
      query?: string;
      semanticQuery?: string;
      [key: string]: any;
    } = {
      maxVersionType: VersionedEntityType.PUBLISHED
    };
    if (params.query) {
      qParams = { ...qParams, query: params.query };
    }
    if (params.semanticQuery) {
      qParams = { ...qParams, semanticQuery: params.semanticQuery };
    }
    if (params.filters) {
      qParams = { ...qParams, ...params.filters };
    }

    let q = new URLSearchParams(qParams).toString();

    await callHttpGet(get, set, APIRequestLabel.GET_GOALS, `/goals?${q}`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_GOALS];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({
      goals: apiCallStatus.response?.goals
    });
  };
};

const getGoalsLibrary = (get, set) => {
  return async (params?: {
    query?: string;
    semanticQuery?: string;
    filters?: Record<any, boolean>;
  }) => {
    params = params || {};
    let qParams = {};
    if (params.query) {
      qParams['query'] = params.query;
    }
    if (params.semanticQuery) {
      qParams['semanticQuery'] = params.semanticQuery;
    }

    if (params.filters) {
      qParams = { ...qParams, ...params.filters };
    }

    // DEFAULT MANDAROTY VALUES
    qParams = {
      ...qParams,
      created: true,
      maxVersionType: VersionedEntityType.DRAFT
    };

    let q = new URLSearchParams(qParams).toString();
    await callHttpGet(get, set, APIRequestLabel.GET_GOALS_LIBRARY, `/goals?${q}`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_GOALS_LIBRARY];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }
    set({
      goalsLibrary: apiCallStatus.response?.goals
    });
  };
};
/**
 * @deprecated Use getGoal instead
 */
const getGoalOld = (get, set) => {
  return async (id: string, maxVersionType?: VersionedEntityType) => {
    if (!maxVersionType) {
      maxVersionType = VersionedEntityType.PUBLISHED;
    }

    const params = {
      maxVersionType: maxVersionType
    };
    let q = new URLSearchParams(params).toString();
    await callHttpGet(get, set, APIRequestLabel.GET_GOAL, `/goals/${id}?${q}`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_GOAL];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({ goal: apiCallStatus.response });
    return apiCallStatus.response;
  };
};

const updateGoal = (get, set) => {
  // Returns a request-scoped success flag: the apiCallStatus slot is read synchronously right
  // after this request's own await, before any concurrent updateGoal can overwrite the shared
  // slot. Callers must trust this value rather than re-reading the slot after awaiting.
  return async (goalEntityId: string, goal: GoalPatchModel): Promise<boolean> => {
    await callHttpPut(get, set, APIRequestLabel.UPDATE_GOAL, `/goals/${goalEntityId}`, goal);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_GOAL];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return false;
    }

    set({
      goal: {
        ...get().goal,
        ...goal,
        availableVersionTypes: [VersionedEntityType.DRAFT, VersionedEntityType.PUBLISHED],
        versionType: VersionedEntityType.DRAFT
      }
    });
    return true;
  };
};

const publishGoal = (get, set) => {
  return async (goalEntityId: string): Promise<boolean> => {
    await callHttpPost(get, set, APIRequestLabel.PUBLISH_GOAL, `/goals/${goalEntityId}/publish`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.PUBLISH_GOAL];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      throw new Error('Failed to publish the goal');
    }

    set({
      goal: {
        ...get().goal,
        versionType: VersionedEntityType.PUBLISHED
      }
    });

    return true;
  };
};

const deleteGoal = (get, set) => {
  return async (goalEntityId: string) => {
    await callHttpDelete(get, set, APIRequestLabel.DELETE_GOAL, `/goals/${goalEntityId}`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.DELETE_GOAL];
    if (apiCallStatus.error) {
      return Promise.reject(apiCallStatus.error);
    }
    set({
      goals: get().goals.filter((goal) => goal.entityId !== goalEntityId),
      goalsLibrary: get().goalsLibrary.filter((goal) => goal.entityId !== goalEntityId)
    });
  };
};

export {
  createGoal,
  deleteGoal,
  getGoalOld,
  getGoalsLibrary,
  getGoalsOld,
  publishGoal,
  updateGoal
};
